import React, { FC, useEffect } from 'react';
import Button from 'src/common/components/Button';
import Modal from 'src/common/components/Modal';
import { trackEvent } from 'src/common/tracking';
import UpdateAvailabilityHeader from './UpdateAvailabilityHeader';
import GA_EVENTS from './constants';
import s from './UpdateAvailability.module.scss';

// Utility function to format the availability range
const formatAvailabilityRange = (
  formattedStartDate: string,
  formattedEndDate: string,
  startTime: string,
  endTime: string,
): string => {
  if (formattedStartDate === formattedEndDate) {
    // Single day with specific times
    return `${startTime} – ${endTime} on ${formattedStartDate}`;
  }
  // Multi-day with specific times
  return `${formattedStartDate} from ${startTime} until ${endTime} on ${formattedEndDate}`;
};

type UpdateAvailabilityPromptProps = {
  boatName: string;
  closeModal: () => void;
  isOpen: boolean;
  startTime: string; // e.g., "3:00 PM"
  endTime: string; // e.g., "6:00 PM"
  formattedStartDate: string;
  formattedEndDate: string;
  onYes: () => void;
  onEdit: () => void;
};

const UpdateAvailabilityPrompt: FC<UpdateAvailabilityPromptProps> = ({
  boatName,
  closeModal,
  formattedStartDate,
  formattedEndDate,
  onYes,
  onEdit,
  isOpen,
  startTime,
  endTime,
}) => {
  useEffect(() => {
    if (isOpen) {
      trackEvent(GA_EVENTS.UPDATE_CALENDAR_PROMPT.OPEN);
    }
  }, [isOpen]);

  return (
    <Modal
      disableOverlayClick
      name="update-availability-modal"
      closeModal={() => {
        closeModal();
        trackEvent(GA_EVENTS.UPDATE_CALENDAR_PROMPT.CLOSE);
      }}
      modalOpen={isOpen}
      label="Update Calendar Availability"
      classNameModifier="medium updateAvailabilityPrompt"
    >
      <UpdateAvailabilityHeader
        title="Update Calendar Availability?"
        description={(
          <>
            Would you like to block{' '}
            {formatAvailabilityRange(formattedStartDate, formattedEndDate, startTime, endTime)}
            {' '}for {boatName}?
          </>
        )}
        variant="prompt"
      />
      <div className={s.buttons}>
        <Button
          type="button"
          classNameModifier="tertiary noMargin"
          onClick={() => {
            onEdit();
            trackEvent(GA_EVENTS.UPDATE_CALENDAR_PROMPT.EDIT);
          }}
        >
          Edit
        </Button>
        <Button
          type="button"
          classNameModifier="tertiary noMargin"
          onClick={() => {
            closeModal();
            trackEvent(GA_EVENTS.UPDATE_CALENDAR_PROMPT.NO);
          }}
        >
          No
        </Button>
        <Button
          type="button"
          classNameModifier="primary noMargin"
          onClick={() => {
            onYes();
            closeModal();
            trackEvent(GA_EVENTS.UPDATE_CALENDAR_PROMPT.YES);
          }}
        >
          Yes
        </Button>
      </div>
    </Modal>
  );
};

export default UpdateAvailabilityPrompt;
